import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Download, Upload, Search } from "lucide-react";
import { api } from "./api";

const STAGES = ["Research", "Ready for Outreach", "Contacted", "Follow-up", "Discovery Scheduled", "Discovery Completed", "Proposal Sent", "Decision Pending", "Booked", "Event Planning", "Completed", "Repeat Client", "Annual Partner", "Not Now", "Closed/Lost"];
const FIELD = "w-full rounded-xl bg-white border border-border px-4 py-2.5 outline-none focus:ring-2 focus:ring-coral focus:border-transparent";

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  if (!lines.length) return [];
  const headers = lines[0].split(",").map(h => h.trim());
  return lines.slice(1).map(line => {
    const values = line.match(/(?:^|,)("(?:[^"]|"")*"|[^,]*)/g).map(v => v.replace(/^,/, "").replace(/^"|"$/g, "").replace(/""/g, '"'));
    const obj = {}; headers.forEach((h, i) => { obj[h] = (values[i] || "").trim(); });
    return obj;
  });
}

export default function Leads() {
  const [params, setParams] = useSearchParams();
  const stage = params.get("stage") || "";
  const q = params.get("q") || "";
  const view = params.get("view") || "table";
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ community: "", contact_name: "", email: "", phone: "", community_type: "", stage: "Research" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = {}; if (stage) p.stage = stage; if (q) p.q = q;
      const { data } = await api().get("/admin/leads", { params: p });
      setLeads(data);
    } finally { setLoading(false); }
  }, [stage, q]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.community) return toast.error("Community/organization is required.");
    try {
      await api().post("/admin/leads", form);
      toast.success("Lead created.");
      setShowNew(false); setForm({ community: "", contact_name: "", email: "", phone: "", community_type: "", stage: "Research" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create lead."); }
  };

  const importCsv = async (file) => {
    const text = await file.text();
    const rows = parseCsv(text);
    if (!rows.length) return toast.error("No rows detected.");
    const { data } = await api().post("/admin/leads/import", rows);
    toast.success(`Imported ${data.created} lead(s).`);
    load();
  };

  const exportCsv = async () => {
    const { data } = await api().get("/admin/leads/export");
    const blob = new Blob([data], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "leads.csv"; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="admin-leads-page">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search size={16} className="absolute top-3 left-3 text-charcoal/40" />
          <input data-testid="leads-search" placeholder="Search name, community, email…" className={`${FIELD} pl-9 min-w-[240px]`}
            defaultValue={q} onKeyDown={(e) => { if (e.key === "Enter") { const p = new URLSearchParams(params); p.set("q", e.target.value); setParams(p); } }} />
        </div>
        <select data-testid="leads-stage-filter" className={FIELD + " max-w-[220px]"} value={stage}
          onChange={(e) => { const p = new URLSearchParams(params); if (e.target.value) p.set("stage", e.target.value); else p.delete("stage"); setParams(p); }}>
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s}>{s}</option>)}
        </select>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button data-testid="leads-view-toggle" onClick={() => { const p = new URLSearchParams(params); p.set("view", view === "table" ? "board" : "table"); setParams(p); }}
            className="rounded-full bg-white border border-border px-4 py-2.5 text-sm font-semibold">{view === "table" ? "Board view" : "Table view"}</button>
          <label className="cursor-pointer rounded-full bg-white border border-border px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1">
            <Upload size={14} /> Import CSV
            <input data-testid="leads-import" type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files[0] && importCsv(e.target.files[0])} />
          </label>
          <button data-testid="leads-export" onClick={exportCsv} className="rounded-full bg-white border border-border px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1"><Download size={14} /> Export</button>
          <button data-testid="leads-new-btn" onClick={() => setShowNew(!showNew)} className="rounded-full bg-coral text-cream px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1"><Plus size={14} /> New lead</button>
        </div>
      </div>

      {showNew && (
        <form onSubmit={create} data-testid="new-lead-form" className="rounded-2xl bg-white border border-border p-5 grid sm:grid-cols-3 gap-3">
          <input data-testid="new-community" required placeholder="Community / organization *" className={FIELD} value={form.community} onChange={(e) => setForm({ ...form, community: e.target.value })} />
          <input data-testid="new-contact-name" placeholder="Contact name" className={FIELD} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          <input data-testid="new-email" type="email" placeholder="Email" className={FIELD} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="new-phone" placeholder="Phone" className={FIELD} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <select data-testid="new-community-type" className={FIELD} value={form.community_type} onChange={(e) => setForm({ ...form, community_type: e.target.value })}>
            <option value="">Community type…</option>
            {["Independent Living", "Assisted Living", "Memory Care", "Active Adult", "Other"].map(t => <option key={t}>{t}</option>)}
          </select>
          <select data-testid="new-stage" className={FIELD} value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
            {STAGES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <button data-testid="new-lead-submit" type="submit" className="col-span-full sm:col-span-1 rounded-full bg-coral text-cream font-semibold px-5 py-2.5">Create lead</button>
        </form>
      )}

      {loading ? <p className="text-charcoal/60">Loading…</p> :
        view === "board" ? (
          <div className="grid grid-flow-col auto-cols-[260px] gap-4 overflow-x-auto pb-4" data-testid="leads-board">
            {STAGES.map((s) => {
              const items = leads.filter(l => l.stage === s);
              return (
                <div key={s} className="rounded-2xl bg-white border border-border p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-poppins font-bold text-sm">{s}</p>
                    <span className="text-xs bg-cream rounded-full px-2 py-0.5">{items.length}</span>
                  </div>
                  <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                    {items.map(l => (
                      <Link key={l.id} to={`/admin/leads/${l.id}`} className="block rounded-xl border border-border p-3 hover:bg-cream">
                        <p className="font-semibold text-sm text-charcoal">{l.community || "(no community)"}</p>
                        <p className="text-xs text-charcoal/60">{l.contact_name || l.name || "—"}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-2xl bg-white border border-border overflow-hidden" data-testid="leads-table">
            <table className="w-full text-sm">
              <thead className="bg-cream">
                <tr className="text-left text-charcoal/60 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3">Community</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Stage</th>
                  <th className="px-4 py-3">Next action</th>
                  <th className="px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => {
                  const overdue = l.next_action_date && new Date(l.next_action_date) < new Date();
                  return (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-border hover:bg-cream/50">
                      <td className="px-4 py-3"><Link to={`/admin/leads/${l.id}`} className="font-semibold text-charcoal hover:text-coral">{l.community || "(no community)"}</Link></td>
                      <td className="px-4 py-3">{l.contact_name || l.name || "—"}</td>
                      <td className="px-4 py-3 text-charcoal/70">{l.email || "—"}</td>
                      <td className="px-4 py-3"><span className="rounded-full bg-sky/40 px-2 py-1 text-xs font-semibold">{l.stage}</span></td>
                      <td className={`px-4 py-3 ${overdue ? "text-coral font-semibold" : "text-charcoal/70"}`}>{l.next_action_date || "—"}</td>
                      <td className="px-4 py-3 text-charcoal/50 text-xs">{l.updated_at?.slice(0, 10)}</td>
                    </tr>
                  );
                })}
                {!leads.length && <tr><td colSpan={6} className="px-4 py-10 text-center text-charcoal/50">No leads. Create one or import a CSV.</td></tr>}
              </tbody>
            </table>
          </div>
        )
      }
    </div>
  );
}
